import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Clock, Calendar, ArrowRight } from 'lucide-react';
import { AUTHOR_IMAGES } from '../../data/authors';

interface BlogCardProps {
    id: number;
    title: string;
    slug: string;
    excerpt: string;
    author: string;
    publishedDate: string;
    featuredImage: string;
    categories: string[];
    tags: string[];
    readTime: number;
}

export default function BlogCard({
    title,
    slug,
    excerpt,
    author,
    publishedDate,
    featuredImage,
    categories,
    readTime
}: BlogCardProps) {
    // Get author image or fallback
    const authorImage = AUTHOR_IMAGES[author] || `https://ui-avatars.com/api/?name=${encodeURIComponent(author)}&background=random&color=fff`;

    return (
        <Link
            to={`/blog/${slug}`}
            className="blog-card group block bg-[#293039] rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-[0_20px_40px_-5px_rgba(0,0,0,0.3)] hover:-translate-y-2 h-full flex flex-col border border-white/5"
        >
            {/* Featured Image */}
            <div className="relative h-64 overflow-hidden">
                {featuredImage ? (
                    <img
                        src={featuredImage}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></svg>
                    </div>
                )}

                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#293039] via-transparent to-transparent opacity-80" />

                {/* Categories Badge */}
                {categories.length > 0 && (
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                        <span className="bg-[#d68800] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-sm">
                            {categories[0]}
                        </span>
                    </div>
                )}

                {/* Read Time Badge */}
                <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10">
                    <Clock className="w-3 h-3" />
                    {readTime} min
                </div>
            </div>

            {/* Content */}
            <div className="p-8 flex flex-col flex-grow relative">
                {/* Date */}
                <div className="flex items-center gap-2 text-gray-400 text-sm font-medium mb-4">
                    <Calendar className="w-4 h-4 text-[#d68800]" />
                    {format(new Date(publishedDate), 'MMM dd, yyyy')}
                </div>

                {/* Title */}
                <h3
                    className="text-2xl font-bold mb-4 text-white group-hover:text-[#d68800] transition-colors duration-300 leading-tight"
                    style={{ fontFamily: 'Sofia Pro, sans-serif' }}
                >
                    {title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-400 mb-6 line-clamp-3 leading-relaxed flex-grow" style={{ fontFamily: 'Sofia Pro, sans-serif', fontSize: '1rem' }}>
                    {excerpt}
                </p>

                {/* Footer */}
                <div className="pt-6 border-t border-white/10 flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden border border-gray-600">
                            <img
                                src={authorImage}
                                alt={author}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    // Fallback if image fails to load
                                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(author)}&background=random`;
                                }}
                            />
                        </div>
                        <span className="text-sm font-medium text-gray-300 truncate max-w-[120px]" style={{ fontFamily: 'Sofia Pro, sans-serif' }}>
                            {author}
                        </span>
                    </div>

                    <span className="text-[#d68800] font-semibold text-sm flex items-center gap-1 group/link group-hover:translate-x-1 transition-transform duration-300">
                        Read <ArrowRight className="w-4 h-4" />
                    </span>
                </div>
            </div>
        </Link>
    );
}
